// store/auth.js
import { create } from "zustand";

// We'll implement simple dual-storage (localStorage for "remember me", sessionStorage otherwise).
// IMPORTANT: Do NOT read from storage during module initialization — that causes SSR/CSR hydration
// mismatches. Instead call `rehydrateAuth()` from a client-only effect (e.g. in Navbar or root layout)
// that runs after mount.
const STORAGE_KEY = "auth-storage";

function saveState(state, remember) {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.stringify(state);
    if (remember) {
      localStorage.setItem(STORAGE_KEY, raw);
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, raw);
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    // ignore
  }
}

const useAuthStore = create((set, get) => ({
  // auth state (server-safe defaults)
  user: null,
  accessToken: null,
  refreshToken: null,
  pendingEmail: null,
  resetToken: null,
  remember: false,

  // set auth data after login or register
  // Accept optional `user` to set user object at the same time.
  setAuth: ({ accessToken, refreshToken, remember = false, user = undefined }) => {
    set((s) => {
      const next = { ...s };
      if (user !== undefined) next.user = user;
      next.accessToken = accessToken;
      next.refreshToken = refreshToken;
      next.remember = remember;
      // persist to chosen storage
      saveState(
        {
          user: next.user,
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
          pendingEmail: next.pendingEmail,
          resetToken: next.resetToken,
          remember: next.remember,
        },
        remember
      );
      const ret = { accessToken, refreshToken, remember };
      if (user !== undefined) ret.user = user;
      return ret;
    });
  },

  setUser: (user) => {
    set((s) => {
      const next = { ...s, user };
      // persist current tokens + user using existing remember flag
      saveState(
        {
          user: next.user,
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
          pendingEmail: next.pendingEmail,
          resetToken: next.resetToken,
          remember: next.remember,
        },
        next.remember
      );
      return { user };
    });
  },

  // for sign up and forgot password flows
  setPendingEmail: (email) => {
    set((s) => {
      const next = { ...s, pendingEmail: email };
      saveState(
        {
          user: next.user,
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
          pendingEmail: next.pendingEmail,
          resetToken: next.resetToken,
          remember: next.remember,
        },
        next.remember
      );
      return { pendingEmail: email };
    });
  },

  // Reset token for password reset
  setResetToken: (code) => {
    set((s) => {
      const next = { ...s, resetToken: code };
      saveState(
        {
          user: next.user,
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
          pendingEmail: next.pendingEmail,
          resetToken: next.resetToken,
          remember: next.remember,
        },
        next.remember
      );
      return { resetToken: code };
    });
  },

  // clear all auth data
  clearAuth: () => {
    set(() => {
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }
      return { user: null, accessToken: null, refreshToken: null, remember: false };
    });
  },
}));

// Client-side rehydration helper - call once after mount to populate the store from storage
export function rehydrateAuth() {
  if (typeof window === "undefined") return;
  try {
    const rawLocal = localStorage.getItem(STORAGE_KEY);
    const rawSession = sessionStorage.getItem(STORAGE_KEY);
    const raw = rawLocal || rawSession;
    if (!raw) return;
    const data = JSON.parse(raw);
    const { accessToken, refreshToken, user, remember } = data;
    const s = useAuthStore.getState();
    // Use setAuth so persistence and flags are applied consistently
    s.setAuth({ accessToken, refreshToken, remember: !!remember, user });
    if (user) s.setUser(user);
  } catch (e) {
    // ignore rehydrate errors
  }
}

export default useAuthStore;
